/**
 * COPY of `functions/src/inventory-app/stock-summary.rules.ts` (kz2 M12 leftovers) — the app reads the Inventory
 * summary (`inventory-summary/{tenantId}`) with the same rules the triggers write it with. Do not edit below this
 * header: change the server file and copy it again (`tools/check-mirrored-rules.mjs` fails on any difference).
 */
/**
 * The Inventory summary's pure rules (kz2 M12 leftovers): `inventory-summary/{tenantId}`, kept by the triggers in
 * `stock-summary.triggers.ts` and the backfill (`scripts/backfill-stock-summary.js`), read by the shell so the
 * Inventory attention badge shows before the person opens Inventory (and its eight streams).
 *
 * The figures are the app's: an item needs attention exactly when `needsAttentionAt(…, EVERYWHERE, 'low' | 'out')`
 * says so (`inventory-items.ts`: carried, `isTracked`, `statusOf(stockOnHand, reorderPoint)`), a count is open when
 * it is not posted, a transfer is on the road when `in-transit`, an order awaits a delivery when sent or part-received
 * (`inventory-orders.ts`, `awaitingDelivery`).
 *
 * The app holds a hand-kept COPY at `src/app/features/erp-apps/inventory/core/rules/stock-summary.rules.ts` (it reads
 * the document with `summaryCounts`) — the functions build cannot import the app, and `prepare` overwrites
 * `functions/src/models`, so neither copy lives there. `tools/check-mirrored-rules.mjs` keeps them identical;
 * `stock-summary.rules.spec.ts` here (jest) and the app's spec (Karma) pin the SAME figures, and the app's spec also
 * holds the copy against `statusOf` / `ivKindOf` themselves.
 */

/** One document per business, its id the tenant id. Written only by the functions (rules: `write: if false`). */
export const INVENTORY_SUMMARY_COLLECTION = 'inventory-summary';

/** Why an item is in the summary: `l` low (at or below its reorder point), `o` out (nothing on hand). */
export type AttentionMark = 'l' | 'o';

/** The overlay facts the summary reads (`tenant-catalog-items/{tenantId}__{productId}`). */
export interface SummaryOverlay {
  readonly productId?: unknown;
  readonly tenantId?: unknown;
  readonly stockOnHand?: unknown;
  readonly reorderPoint?: unknown;
  readonly isCarried?: unknown;
  readonly trackStock?: unknown;
}

/** The one catalogue fact it reads (`catalog-products/{productId}`). */
export interface SummaryProduct {
  readonly productNature?: unknown;
}

/**
 * `inventory-summary/{tenantId}`. `attention` is SPARSE — only the items that need attention, by product id — so a
 * trigger that runs twice (Firestore triggers are at-least-once) writes the same entry again instead of counting it
 * twice. The three counts are recounted by query on every change, never incremented, for the same reason.
 */
export interface InventorySummaryDoc {
  readonly id?: string;
  readonly tenantId?: string;
  readonly attention?: Readonly<Record<string, AttentionMark>>;
  /** Counts not yet posted (`inventory.counts_open`, the `counts` capability's open work). */
  readonly openCounts?: number;
  /** Transfers on the road (`inventory.in_transit`, the `transfers` capability's open work). */
  readonly inTransit?: number;
  /** Purchase orders sent or partly received (`inventory.orders_awaiting`). */
  readonly ordersAwaiting?: number;
  readonly updatedAt?: unknown;
}

/** What the badges read off the document. */
export interface SummaryCounts {
  readonly out: number;
  readonly low: number;
  readonly openCounts: number;
  readonly inTransit: number;
  readonly ordersAwaiting: number;
}

/** Low, out or fine against a reorder point — the app's `statusOf`. */
export function summaryStatusOf(qty: number, reorder: number | null): 'ok' | 'low' | 'out' {
  if (qty <= 0) {
    return 'out';
  }
  if (reorder != null && qty <= reorder) {
    return 'low';
  }
  return 'ok';
}

/** Whether a line holds stock — the app's `ivKindOf(nature, trackStock) === 'product'` (`isTracked`). */
export function isTrackedNature(nature: unknown, trackStock: unknown): boolean {
  if (nature === 'service' || nature === 'kit' || nature === 'consumable') {
    return false;
  }
  return trackStock !== false;
}

/**
 * The mark from the overlay alone, as if the product were tracked: `null` for no overlay, a de-listed one
 * (`isCarried: false`), one that tracks no stock, or stock that is fine. A trigger whose before and after marks are
 * equal has nothing to change and reads nothing more.
 */
export function overlayMark(o: SummaryOverlay | null | undefined): AttentionMark | null {
  if (!o || o.isCarried === false || o.trackStock === false) {
    return null;
  }
  /* Read as `ivItemOf` reads them — a value that is no number compares false, as it does in the app. */
  const qty = Number(o.stockOnHand ?? 0);
  const reorder = o.reorderPoint == null ? null : Number(o.reorderPoint);
  const status = summaryStatusOf(qty, reorder);
  return status === 'out' ? 'o' : status === 'low' ? 'l' : null;
}

/** The item's entry in `attention`: the overlay's mark, when the catalogue says the line holds stock at all. */
export function attentionMark(overlay: SummaryOverlay | null | undefined, product: SummaryProduct | null | undefined): AttentionMark | null {
  const mark = overlayMark(overlay);
  if (!mark || !product || !isTrackedNature(product.productNature, overlay?.trackStock)) {
    return null;
  }
  return mark;
}

/** `tenant-catalog-items/{tenantId}__{productId}`: the product id, from the row or else its document id. */
export function summaryProductIdOf(docId: string, overlay: SummaryOverlay | null | undefined): string | null {
  if (typeof overlay?.productId === 'string' && overlay.productId) {
    return overlay.productId;
  }
  const at = docId.indexOf('__');
  return at > 0 && at + 2 < docId.length ? docId.slice(at + 2) : null;
}

/** A count still open: anything not posted (`status !== 'posted'`, a missing status included). */
export function isOpenCount(c: { readonly status?: unknown } | null | undefined): boolean {
  return !!c && c.status !== 'posted';
}

/** A transfer on the road. */
export function isInTransit(t: { readonly status?: unknown } | null | undefined): boolean {
  return !!t && t.status === 'in-transit';
}

/** A purchase order something can still arrive against — sent or part-received, never a draft (`awaitingDelivery`). */
export function isAwaitingDelivery(o: { readonly status?: unknown } | null | undefined): boolean {
  return !!o && (o.status === 'sent' || o.status === 'partial');
}

/** The badges' figures from the document; zero for a missing one or a missing field. */
export function summaryCounts(doc: InventorySummaryDoc | null | undefined): SummaryCounts {
  let out = 0;
  let low = 0;
  for (const mark of Object.values(doc?.attention ?? {})) {
    if (mark === 'o') out++;
    else if (mark === 'l') low++;
  }
  const n = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0);
  return { out, low, openCounts: n(doc?.openCounts), inTransit: n(doc?.inTransit), ordersAwaiting: n(doc?.ordersAwaiting) };
}
